import React from 'react';
import { Phone, MessageCircle, Clock, Headphones, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const Footer = ({ onRegisterClick }) => {
  const waUrl = `https://api.whatsapp.com/send?phone=${process.env.REACT_APP_WHATSAPP_PHONE}&text=I%27m%20interested%20UniFi%20Service`;
  return (
    <footer
      className="relative text-white overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #0E1424 0%, #131A2E 50%, #0B1020 100%)',
        fontFamily: 'Inter, sans-serif',
      }}
    >
      {/* Decorative orange glow */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: '-120px',
          right: '-120px',
          width: '420px',
          height: '420px',
          background: 'radial-gradient(circle, rgba(255,102,0,0.18) 0%, rgba(255,102,0,0) 70%)',
          pointerEvents: 'none',
        }}
      />
      <div
        aria-hidden
        style={{
          position: 'absolute',
          bottom: '-150px',
          left: '-100px',
          width: '380px',
          height: '380px',
          background: 'radial-gradient(circle, rgba(23,1,228,0.18) 0%, rgba(23,1,228,0) 70%)',
          pointerEvents: 'none',
        }}
      />
      {/* Subtle grid texture */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          pointerEvents: 'none',
        }}
      />

      <div className="container mx-auto px-6 pt-16 pb-32 relative">
        {/* Top grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-14">
          {/* Left: branding + tagline */}
          <div>
            <img
              src="https://customer-assets.emergentagent.com/job_unifi-platform/artifacts/4nqdfull_logo%402x.png"
              alt="TM Penjual Sah Authorised Reseller"
              className="h-16 mb-7"
              style={{ filter: 'drop-shadow(0 8px 16px rgba(255,102,0,0.25))' }}
            />
            <h3
              style={{
                fontSize: '30px',
                fontWeight: 800,
                lineHeight: 1.15,
                letterSpacing: '-0.01em',
                marginBottom: '14px',
                background: 'linear-gradient(90deg, #ffffff 0%, #FFB987 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Apply anytime you want,<br />wherever you are.
            </h3>
            <div className="flex items-center gap-2 mb-3 text-gray-300">
              <Clock className="w-4 h-4 text-[#FF6600]" />
              <p className="text-sm font-medium">We&apos;re open 24 hours a day, 7 days a week.</p>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed max-w-md">
              Your application will be processed immediately and you will be informed the next working day.
            </p>
          </div>

          {/* Right: contact cards */}
          <div className="flex flex-col gap-4 md:items-end">
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-sales-link"
              className="group flex items-center gap-4 w-full md:w-[360px] rounded-2xl px-5 py-4 transition-all"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,102,0,0.25)',
                backdropFilter: 'blur(12px)',
              }}
            >
              <div
                className="flex items-center justify-center flex-shrink-0 rounded-xl"
                style={{
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(135deg, #FF6600 0%, #FF8A3D 100%)',
                  boxShadow: '0 8px 20px rgba(255,102,0,0.35)',
                }}
              >
                <MessageCircle className="w-6 h-6 text-white" />
              </div>
              <div className="flex-grow">
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#FF6600] font-bold mb-1">Sales / Enquiry</p>
                <p className="text-lg font-bold text-white group-hover:text-[#FF8A3D] transition-colors">{process.env.REACT_APP_WHATSAPP_DISPLAY}</p>
              </div>
              <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-[#FF6600] group-hover:translate-x-1 transition-all" />
            </a>

            <a
              href="tel:100"
              data-testid="footer-support-link"
              className="group flex items-center gap-4 w-full md:w-[360px] rounded-2xl px-5 py-4 transition-all"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                backdropFilter: 'blur(12px)',
              }}
            >
              <div
                className="flex items-center justify-center flex-shrink-0 rounded-xl"
                style={{
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(135deg, #1701E4 0%, #4F47FF 100%)',
                  boxShadow: '0 8px 20px rgba(23,1,228,0.35)',
                }}
              >
                <Headphones className="w-6 h-6 text-white" />
              </div>
              <div className="flex-grow">
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#7C8AFF] font-bold mb-1">Bill / Support</p>
                <p className="text-lg font-bold text-white group-hover:text-[#A9B3FF] transition-colors">100 / 03-22411290</p>
              </div>
              <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-[#7C8AFF] group-hover:translate-x-1 transition-all" />
            </a>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mb-12">
          <Button
            onClick={onRegisterClick}
            data-testid="footer-register-btn"
            className="group bg-gradient-to-r from-[#FF6600] to-[#FF8A3D] hover:from-[#E55A00] hover:to-[#FF6600] text-white px-12 py-6 text-base font-bold rounded-full transition-all transform hover:scale-105"
            style={{
              boxShadow: '0 16px 40px rgba(255,102,0,0.35)',
              letterSpacing: '0.05em',
            }}
          >
            <span>REGISTER UNIFI TODAY!</span>
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>

        {/* Divider */}
        <div
          style={{
            height: '1px',
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)',
            marginBottom: '24px',
          }}
        />

        {/* Bottom row */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-xs text-gray-500">Unifi. All rights reserved.</p>
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full"
            style={{
              background: 'rgba(255,102,0,0.08)',
              border: '1px solid rgba(255,102,0,0.25)',
            }}
          >
            <span style={{ width: '6px', height: '6px', background: '#FF6600', borderRadius: '50%' }} />
            <span className="text-[11px] font-semibold tracking-wider uppercase text-[#FFB987]">Authorised Reseller</span>
          </div>
        </div>
      </div>

      {/* Floating WhatsApp Tab - Pinned to right edge */}
      <a
        href={waUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="footer-whatsapp-floating"
        aria-label="Chat on WhatsApp"
        className="group fixed z-[60]"
        style={{
          right: 0,
          top: '58%',
          transform: 'translateY(-50%)',
          width: '64px',
          height: '76px',
          background: '#25D366',
          borderRadius: '14px 0 0 14px',
          boxShadow: '-6px 8px 24px rgba(37,211,102,0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          transition: 'all 0.25s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.width = '78px';
          e.currentTarget.style.boxShadow = '-10px 10px 30px rgba(37,211,102,0.6)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.width = '64px';
          e.currentTarget.style.boxShadow = '-6px 8px 24px rgba(37,211,102,0.45)';
        }}
      >
        <img
          src="https://customer-assets.emergentagent.com/job_unifi-platform/artifacts/4zi63vxy_WhatsAppIcon.jpg"
          alt="WhatsApp"
          className="transition-transform duration-300 group-hover:scale-110"
          style={{ width: '85%', height: '85%', objectFit: 'contain', display: 'block' }}
        />
      </a>
    </footer>
  );
};

export default Footer;
